import React, { useMemo } from 'react';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { Box, Typography } from '@mui/material';
import { useDashboard } from '../../../contexts/DashboardContext';

const COLORS = ['#1976d2', '#2e7d32', '#ed6c02', '#9c27b0', '#d32f2f'];

function resolveYAxisDomain(raw, config, chartData, keys) {
  const r = raw && typeof raw === 'object' ? raw : {};
  const c = config && typeof config === 'object' ? config : {};
  const ymin = c.yMin ?? c.yDomainMin ?? r.yMin ?? (Array.isArray(r.domain) ? r.domain[0] : r.domain?.min);
  const ymax = c.yMax ?? c.yDomainMax ?? r.yMax ?? (Array.isArray(r.domain) ? r.domain[1] : r.domain?.max);
  const lo = ymin != null && Number.isFinite(Number(ymin)) ? Number(ymin) : null;
  const hi = ymax != null && Number.isFinite(Number(ymax)) ? Number(ymax) : null;
  if (lo != null && hi != null) return [lo, hi];
  if (lo != null) return [lo, 'auto'];
  if (hi != null) return ['auto', hi];
  if (!chartData.length || !keys.length) return undefined;
  let minV = Infinity;
  let maxV = -Infinity;
  chartData.forEach((row) => {
    keys.forEach((k) => {
      const v = Number(row[k]);
      if (Number.isFinite(v)) {
        minV = Math.min(minV, v);
        maxV = Math.max(maxV, v);
      }
    });
  });
  if (minV === Infinity) return undefined;
  const span = maxV - minV;
  const pad = span === 0 ? Math.abs(maxV || 1) * 0.05 || 0.5 : span * 0.06;
  let y0 = minV - pad;
  let y1 = maxV + pad;
  if (minV >= 0 && y0 < 0) y0 = 0;
  return [y0, y1];
}

export default function DashboardChart({ panelId, outputKey = 'chart_data', config }) {
  const { getSlice } = useDashboard();
  const raw = getSlice(panelId, outputKey);
  const chartType = config?.chartType || 'bar';

  const chartData = useMemo(() => {
    if (!raw || typeof raw !== 'object') return [];
    const labels = raw.labels ?? [];
    const datasets = raw.datasets ?? [];
    if (!labels.length || !datasets.length) return [];
    return labels.map((label, i) => {
      const point = { name: String(label) };
      datasets.forEach((ds, j) => {
        const key = ds.label || `series_${j}`;
        point[key] = Array.isArray(ds.data) ? ds.data[i] : null;
      });
      return point;
    });
  }, [raw]);

  const keys = useMemo(() => {
    const k = new Set();
    chartData.forEach((d) => Object.keys(d).filter((x) => x !== 'name').forEach((x) => k.add(x)));
    return [...k];
  }, [chartData]);

  const yDomain = useMemo(
    () => (chartType === 'pie' ? undefined : resolveYAxisDomain(raw, config, chartData, keys)),
    [raw, config, chartData, keys, chartType],
  );

  const chartHeight = useMemo(() => {
    const c = config && typeof config === 'object' ? config : {};
    const fromCfg = Number(c.chartMinHeight);
    if (Number.isFinite(fromCfg) && fromCfg >= 160) return Math.min(720, fromCfg);
    const n = chartData.length;
    return Math.min(580, Math.max(220, 200 + n * 22));
  }, [config, chartData.length]);

  if (!chartData.length) {
    return (
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', minHeight: 200 }}>
        <Typography color="text.secondary">No chart data. Use chat or refresh to run a flow.</Typography>
      </Box>
    );
  }

  const yAxisShared = yDomain ? (
    <YAxis tick={{ fontSize: 12 }} domain={yDomain} allowDataOverflow />
  ) : (
    <YAxis tick={{ fontSize: 12 }} />
  );

  const renderChart = () => {
    const margin = { top: 8, right: 8, left: 8, bottom: 8 };
    if (chartType === 'pie') {
      const pieData = keys.map((k) => ({ name: k, value: chartData.reduce((sum, d) => sum + (Number(d[k]) || 0), 0) }));
      return (
        <PieChart margin={margin}>
          <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius="80%" label>
            {pieData.map((_, i) => (
              <Cell key={i} fill={COLORS[i % COLORS.length]} />
            ))}
          </Pie>
          <Tooltip />
          <Legend />
        </PieChart>
      );
    }
    if (chartType === 'line') {
      return (
        <LineChart data={chartData} margin={margin}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="name" tick={{ fontSize: 12 }} interval={0} angle={chartData.length > 10 ? -25 : 0} textAnchor={chartData.length > 10 ? 'end' : 'middle'} height={chartData.length > 10 ? 56 : 30} />
          {yAxisShared}
          <Tooltip />
          <Legend />
          {keys.map((k, i) => (
            <Line key={k} type="monotone" dataKey={k} stroke={COLORS[i % COLORS.length]} strokeWidth={2} dot />
          ))}
        </LineChart>
      );
    }
    if (chartType === 'area') {
      return (
        <AreaChart data={chartData} margin={margin}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="name" tick={{ fontSize: 12 }} interval={0} angle={chartData.length > 10 ? -25 : 0} textAnchor={chartData.length > 10 ? 'end' : 'middle'} height={chartData.length > 10 ? 56 : 30} />
          {yAxisShared}
          <Tooltip />
          <Legend />
          {keys.map((k, i) => (
            <Area key={k} type="monotone" dataKey={k} stackId="1" stroke={COLORS[i % COLORS.length]} fill={COLORS[i % COLORS.length]} fillOpacity={0.6} />
          ))}
        </AreaChart>
      );
    }
    return (
      <BarChart data={chartData} margin={margin}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="name" tick={{ fontSize: 12 }} interval={0} angle={chartData.length > 10 ? -25 : 0} textAnchor={chartData.length > 10 ? 'end' : 'middle'} height={chartData.length > 10 ? 56 : 30} />
        {yAxisShared}
        <Tooltip />
        <Legend />
        {keys.map((k, i) => (
          <Bar key={k} dataKey={k} fill={COLORS[i % COLORS.length]} />
        ))}
      </BarChart>
    );
  };

  return (
    <Box sx={{ width: '100%', height: chartHeight, minHeight: chartHeight, p: 2 }}>
      <ResponsiveContainer width="100%" height="100%">
        {renderChart()}
      </ResponsiveContainer>
    </Box>
  );
}
